import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { User, UserCreate, UserUpdate, UserRole, usersService } from '../services/users';
import { companiesService, Company } from '../services/companies';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/i18nFormat';

const Users: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Verificar se o usuário é CLIENT
  const isClientUser = user?.role === UserRole.CLIENT;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<UserCreate & UserUpdate>();

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        const usersData = await usersService.getAll();
        setUsers(usersData);

        // Usuários CLIENT não precisam carregar lista de empresas
        if (!isClientUser) {
          const companiesData = await companiesService.getAll();
          setCompanies(companiesData);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error(t('users.toasts.loadError'));
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersData = await usersService.getAll();
      setUsers(usersData);

      // Usuários CLIENT não precisam carregar lista de empresas
      if (!isClientUser) {
        const companiesData = await companiesService.getAll();
        setCompanies(companiesData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(t('users.toasts.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const onSubmit = async (data: UserCreate & UserUpdate) => {
    try {
      console.log('Form data received:', data);
      console.log('User context:', { user, isClientUser });

      // Para usuários CLIENT, definir automaticamente o company_id
      if (isClientUser && user?.company_id) {
        data.company_id = user.company_id;
        console.log('Set company_id for CLIENT user:', data.company_id);
      }

      console.log('Final data to be sent:', data);

      if (editingUser) {
        // Update existing user
        const updateData: UserUpdate = { ...data };
        if (!data.password) {
          delete updateData.password;
        }
        await usersService.update(editingUser.id, updateData);
        toast.success(t('users.toasts.updateSuccess'));
      } else {
        // Create new user
        console.log('Creating new user with data:', data);
        await usersService.create(data as UserCreate);
        toast.success(t('users.toasts.createSuccess'));
      }

      setIsModalOpen(false);
      setEditingUser(null);
      reset();
      loadUsers();
    } catch (error: any) {
      console.error('Error saving user:', error);
      const errorMessage = error.response?.data?.detail || t('users.toasts.saveError');
      toast.error(errorMessage);
    }
  };

  // Handle edit
  const handleEdit = (user: User) => {
    setEditingUser(user);
    setValue('username', user.username);
    setValue('email', user.email);
    setValue('full_name', user.full_name);
    setValue('is_active', user.is_active);
    setValue('company_id', user.company_id);
    setValue('role', user.role);
    setIsModalOpen(true);
  };

  // Handle delete
  const handleDelete = async (user: User) => {
    if (window.confirm(t('users.actions.confirmDelete', { username: user.username }))) {
      try {
        await usersService.delete(user.id);
        toast.success(t('users.actions.deletedSuccess'));
        loadUsers();
      } catch (error: any) {
        console.error('Error deleting user:', error);
        const errorMessage = error.response?.data?.detail || t('users.actions.deletedError');
        toast.error(errorMessage);
      }
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    reset();
    setShowPassword(false);
  };

  // Get company name by ID
  const getCompanyName = (companyId: number) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'N/A';
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600'></div>
      </div>
    );
  }

  return (
    <div className='p-6'>
      <div className='flex justify-between items-center mb-6'>
        <h1 className='text-2xl font-bold text-gray-900 dark:text-gray-100'>{t('users.title')}</h1>
        <button
          onClick={() => setIsModalOpen(true)}
          className='bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2'
        >
          <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 6v6m0 0v6m0-6h6m-6 0H6' />
          </svg>
          {t('users.new')}
        </button>
      </div>

      {/* Users Table */}
      <div className='bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden'>
        <table className='min-w-full divide-y divide-gray-200'>
          <thead className='bg-gray-50'>
            <tr>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  {t('users.table.user')}
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  {t('users.table.email')}
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  {t('users.table.company')}
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  {t('users.table.status')}
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  {t('users.table.role')}
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  {t('users.table.createdAt')}
              </th>
              <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  {t('users.table.actions')}
              </th>
            </tr>
          </thead>
          <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
            {users.map(user => (
              <tr key={user.id} className='hover:bg-gray-50'>
                <td className='px-6 py-4 whitespace-nowrap'>
                  <div>
                    <div className='text-sm font-medium text-gray-900 dark:text-white'>{user.full_name}</div>
                    <div className='text-sm text-gray-500'>@{user.username}</div>
                  </div>
                </td>
                <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300'>{user.email}</td>
                <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300'>
                  {getCompanyName(user.company_id)}
                </td>
                <td className='px-6 py-4 whitespace-nowrap'>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {user.is_active ? t('common.active') : t('common.inactive')}
                  </span>
                </td>
                <td className='px-6 py-4 whitespace-nowrap'>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.role === UserRole.ADMINISTRATOR
                        ? 'bg-red-100 text-red-800'
                        : user.role === UserRole.ANALYST
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {user.role === UserRole.ADMINISTRATOR ? t('roles.administrator') :
                        user.role === UserRole.ANALYST ? t('roles.analyst') : t('roles.client')}
                  </span>
                </td>
                <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                    {formatDate(user.created_at, { dateStyle: 'medium' })}
                </td>
                <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                  <button onClick={() => handleEdit(user)} className='text-blue-600 hover:text-blue-900 mr-3'>
                      {t('users.actions.edit')}
                  </button>
                  <button onClick={() => handleDelete(user)} className='text-red-600 hover:text-red-900'>
                      {t('users.actions.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && <div className='text-center py-8 text-gray-500'>{t('users.empty')}</div>}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
          <div className='relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800 dark:border-gray-600'>
            <div className='mt-3'>
              <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-4'>
                  {editingUser ? t('users.edit') : t('users.new')}
              </h3>

              <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-700'>{t('users.form.username')}</label>
                  <input
                    type='text'
                    {...register('username', { required: t('users.form.errors.usernameRequired') })}
                    className='mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                  />
                  {errors.username && <p className='mt-1 text-sm text-red-600'>{errors.username.message}</p>}
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700'>{t('users.form.email')}</label>
                  <input
                    type='email'
                    {...register('email', { required: t('users.form.errors.emailRequired') })}
                    className='mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                  />
                  {errors.email && <p className='mt-1 text-sm text-red-600'>{errors.email.message}</p>}
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700'>{t('users.form.fullName')}</label>
                  <input
                    type='text'
                    {...register('full_name', { required: t('users.form.errors.fullNameRequired') })}
                    className='mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                  />
                  {errors.full_name && <p className='mt-1 text-sm text-red-600'>{errors.full_name.message}</p>}
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700'>
                      {t('users.form.password')} {editingUser ? t('users.form.keepBlank') : ''}
                  </label>
                  <div className='relative'>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      {...register('password', editingUser ? {} : { required: t('users.form.errors.passwordRequired') })}
                      className='mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                    />
                    <button
                      type='button'
                      onClick={() => setShowPassword(!showPassword)}
                      className='absolute inset-y-0 right-0 pr-3 flex items-center'
                    >
                      <svg className='h-5 w-5 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        {showPassword ? (
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21'
                          />
                        ) : (
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                          />
                        )}
                      </svg>
                    </button>
                  </div>
                  {errors.password && <p className='mt-1 text-sm text-red-600'>{errors.password.message}</p>}
                </div>

                {/* Seletor de empresa - apenas para usuários não CLIENT */}
                {!isClientUser && (
                  <div>
                    <label className='block text-sm font-medium text-gray-700'>{t('users.form.company')}</label>
                    <select
                      {...register('company_id', { required: t('users.form.errors.companyRequired') })}
                      className='mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                    >
                      <option value=''>{t('users.form.selectCompany')}</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                    {errors.company_id && <p className='mt-1 text-sm text-red-600'>{errors.company_id.message}</p>}
                  </div>
                )}

                {/* Informação da empresa para usuários CLIENT */}
                {isClientUser && user?.company && (
                  <div>
                    <label className='block text-sm font-medium text-gray-700'>{t('users.form.company')}</label>
                    <div className='mt-1 block w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md'>
                      <span className='text-gray-900 dark:text-gray-300'>{user.company.name}</span>
                      <p className='text-sm text-blue-600 mt-1'>{t('users.form.companyInfoForClient')}</p>
                    </div>
                  </div>
                )}

                <div>
                  <label className='block text-sm font-medium text-gray-700'>{t('users.form.role')}</label>
                  <select
                    {...register('role', { required: t('users.form.errors.roleRequired') })}
                    className='mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                  >
                      <option value=''>{t('users.form.selectRole')}</option>
                      <option value={UserRole.CLIENT}>{t('roles.client')}</option>
                      <option value={UserRole.ANALYST}>{t('roles.analyst')}</option>
                      <option value={UserRole.ADMINISTRATOR}>{t('roles.administrator')}</option>
                  </select>
                  {errors.role && <p className='mt-1 text-sm text-red-600'>{errors.role.message}</p>}
                </div>

                <div className='flex items-center'>
                  <label className='flex items-center'>
                    <input
                      type='checkbox'
                      {...register('is_active')}
                      className='rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                    />
                    <span className='ml-2 text-sm text-gray-700'>{t('users.form.isActive')}</span>
                  </label>
                </div>

                <div className='flex justify-end space-x-3 pt-4'>
                  <button
                    type='button'
                    onClick={handleCloseModal}
                    className='px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md'
                  >
                    {t('users.save.cancel')}
                  </button>
                  <button
                    type='submit'
                    disabled={isSubmitting}
                    className='px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50'
                  >
                      {isSubmitting ? t('users.save.saving') : (editingUser ? t('users.save.update') : t('users.save.create'))}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
